import React from 'react';
import {createContext, useContext, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {ROUTE} from '@/constants/main/route';
import {VOD_TYPES} from '@/constants';
import globalDomains from '@/utils/global-domains';

/**
 * @typedef {Object} BroadcastContextType
 *
 * ────────────── string ──────────────
 * @property {string} broadcastId   방송 아이디
 * @property {'vod'|'live'|string} broadcastType  방송 타입
 * @property {string} broadBps      ??
 * @property {string} broadResolution ??
 * @property {string} broadcastLink 방송 링크
 * @property {string} categoryName  카테고리 이름
 * @property {string} categoryNo    카테고리 번호
 * @property {string} vodCategoryNo VOD 카테고리 번호
 * @property {string} createdDate   생성 날짜
 * @property {string} display       ??
 * @property {string} duration      ??
 * @property {string} originalUserId  원본 유저 아이디
 * @property {string} originalUserNickname  원본 유저 닉네임
 * @property {string} previewThumbnailUrl   미리보기 썸네일 URL
 * @property {string} profileImageUrl       프로필 이미지 URL
 * @property {string} searchActionType      ??
 * @property {string} subscriptionOnly  구독 전용 여부
 * @property {string} thumbnailUrl    썸네일 URL
 * @property {string} title           방송 제목
 * @property {string} updatedDate     수정 날짜
 * @property {string} userId          유저 아이디
 * @property {string} userNickname    유저 닉네임
 * @property {string} vodOriginalId   VOD 고유 아이디
 * @property {string} vodType         VOD 타입
 * @property {string} vodUploaderId   VOD 업로더 아이디
 * @property {string} viewCount       시청자 수
 * @property {string} isDisplay       공개 여부
 * @property {string} liveType        LIVE 타입
 *
 * ────────────── number ──────────────
 * @property {number} accessibleAge   시청 나이
 * @property {number} authNumber      VOD 권한 번호
 * @property {number} lastViewDuration  내가 본 VOD 영상길이
 * @property {number} rankIndex       별별랭킹 인덱스
 * @property {number} totalDuration   전체 VOD 영상길이
 *
 * ────────────── boolean ──────────────
 * @property {boolean} allowsAdult    연령제한 여부
 * @property {boolean} hasBadge       배지 여부
 * @property {boolean} hasDrops       드랍스 여부
 * @property {boolean} hasPassword    비밀번호 여부
 * @property {boolean} isCinety       시네티 여부
 * @property {boolean} isFan          팬 여부
 * @property {boolean} isPpv          PPV 여부
 * @property {boolean} isSubscribe    구독 여부
 * @property {boolean} isTitleHover   제목 Hover 여부
 * @property {boolean} isUserClip     유저클립 여부
 * @property {boolean} canResend      탐방허용 여부
 *
 * ────────────── Array ──────────────
 * @property {string[]} autoHashTags      이벤트 태그 리스트
 * @property {string[]} categoryTags      카테고리 태그 리스트
 * @property {string[]} hashTags      유저 태그 리스트
 * @property {string[]} catchStoryIdList  캐치 스토리 아이디 리스트
 * @property {string[]} titleHistory      ??
 *
 * ────────────── Object ──────────────
 * @property {Object} chipCategoryOptions   칩 카테고리 옵션
 * @property {Object} contextMenu
 * @property {string} contextMenu.appScheme
 * @property {string} contextMenu.shareUrl
 * @property {string} contextMenu.stationNo
 * @property {any[]} contextMenu.menuList
 *
 * @property {Object} blockStreamer       비노출 스트리머 객체
 * @property {string[]} blockStreamer.hiddenStreamerList    숨김 스트리머 리스트
 * @property {string[]} blockStreamer.nonRecommendableStreamerList  비추천 스트리머 리스트
 *
 * @property {Object} log                 Log 객체
 * @property {?string} log.listDataType
 * @property {Object} log.data
 * @property {?string} log.groupName
 * @property {?string} log.groupType
 *
 * ────────────── Function ──────────────
 * @property {Function} handleAfterLogSend  Log Send After 이벤트
 * @property {Function} handleMenuClicked   Context Menu 클릭 이벤트
 * @property {Function} setIsTitleHover     SetIsTitleHover
 */

/** @type {React.Context<BroadcastContextType>} */
const BroadcastContext = createContext({
  userId: '',
  userNickname: '',
  broadcastType: 'vod',
  broadcastId: '',
  updatedDate: '',
  allowsAdult: false,
  hasBadge: false,
  vodType: '',
  liveType: '',
  duration: '',
  lastViewDuration: 0,
  totalDuration: 0,
  originalUserId: '',
  originalUserNickname: '',
  thumbnailUrl: '',
  previewThumbnailUrl: '',
  profileImageUrl: '',
  contextMenu: {
    appScheme: '',
    shareUrl: '',
    stationNo: '',
    menuList: [],
  },
  chipCategoryOptions: {},
  createdDate: '',
  viewCount: '',
  isTitleHover: false,
  authNumber: 0,
  categoryNo: '',
  vodCategoryNo: '',
  accessibleAge: 0,
  hasPassword: false,
  hasDrops: false,
  isPpv: false,
  isCinety: true,
  isSubscribe: false,
  isFan: false,
  isDisplay: '',
  title: '',
  autoHashTags: [],
  categoryName: '',
  titleHistory: [],
  searchActionType: '',
  vodOriginalId: '',
  vodUploaderId: '',
  isUserClip: false,
  canResend: false,
  catchStoryIdList: [],
  broadBps: '',
  broadResolution: '',
  broadcastLink: '',
  rankIndex: 0,
  subscriptionOnly: '0',
  log: {
    listDataType: null,
    data: {},
    groupType: null,
    groupName: null,
  },
  blockStreamer: {
    hiddenStreamerList: [],
    nonRecommendableStreamerList: [],
  },
  handleMenuClicked: () => {},
  handleAfterLogSend: () => {},
  setIsTitleHover: () => {},
});

export const useBroadcastContext = () => {
  const context = useContext(BroadcastContext);
  if (!context) {
    throw new Error(
      'useBroadcastContext must be within a BroadcastInfo Component.',
    );
  }
  return context;
};

const BroadcastProvider = ({broadInfo, children}) => {
  const {
    userId,
    broadcastId,
    updatedDate = '',
    vodType,
    broadcastType,
    handleAfterLogSend,
    broadcastLink,
  } = broadInfo;

  const {VOD_AFREECATV, PLAY_AFREECATV} = globalDomains;
  const {CATCH, CATCH_STORY} = VOD_TYPES;
  const [isTitleHover, setIsTitleHover] = useState(false);
  const location = useLocation();

  if (!broadcastType) {
    throw new Error('broadcastType is required.');
  }

  const getBroadcastLink = (broadcastType) => {
    if (broadcastType === 'vod') {
      if (!broadInfo.vodType) {
        if (process.env.BUILD_ENV === 'develop') {
          throw new Error('vodType is required.');
        } else {
          return;
        }
      }
      if (vodType === 'PLAYLIST') {
        if (!broadInfo.playlistId) {
          throw new Error('playlistId is required.');
        }
        return (
          broadInfo.playlistLink ||
          `${VOD_AFREECATV}/player/${broadcastId}/playlist/${broadInfo.playlistId}`
        );
      }
      return `${VOD_AFREECATV}/player/${broadcastId}${updatedDate && '/later#REG_DATE/1'}${broadInfo.vodType === CATCH ? '/catch' : ''}${broadInfo.vodType === CATCH_STORY ? `/catchstory?szLocation=search&aStoryListIdx=${broadInfo.catchStoryIdList?.join('-')}` : ''}`;
    }

    if (broadcastType === 'live') {
      if (!broadInfo.liveType) {
        if (process.env.BUILD_ENV === 'develop') {
          throw new Error('liveType is required.');
        } else {
          return;
        }
      }
      return `${PLAY_AFREECATV}/${userId}/${broadcastId}${updatedDate && '/later#REG_DATE/1'}`;
    }
  };

  const shouldShowLaterButton = (type, vodType, location) => {
    if (type === 'vod') {
      return (
        vodType !== VOD_TYPES.CATCH &&
        vodType !== VOD_TYPES.CATCH_STORY &&
        location.pathname !== ROUTE.MY.HISTORY.LATER
      );
    } else {
      return location.pathname !== ROUTE.MY.HISTORY.LATER;
    }
  };

  const handleSectionClick = (type) => {
    let param = type ? {type: type} : '';

    handleAfterLogSend?.(param);
  };

  return (
    <BroadcastContext.Provider
      value={{
        isShowChips: true,
        ...broadInfo,
        isTitleHover,
        setIsTitleHover,
        broadcastLink:
          broadcastLink || getBroadcastLink(broadInfo.broadcastType),
        hasShowLaterButton: shouldShowLaterButton(
          broadcastType,
          vodType,
          location,
        ),
        handleSectionClick,
      }}>
      {children}
    </BroadcastContext.Provider>
  );
};

export default BroadcastProvider;
